import { useGetContactStats } from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

/** INR formatter — mirrors account-detail.tsx's formatINR (no shared @/lib/format money fn exists yet). */
function formatINR(amount: number | null | undefined): string {
  const n = Number(amount ?? 0);
  if (!Number.isFinite(n)) return "—";
  return `₹${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(n)}`;
}

function fmtDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  return format(new Date(d), "d MMM yyyy");
}

/** Renders 3 (integer) or 0.8 (one decimal) — matches spec sample "avg 3/mo · 0.8/wk". */
function fmtAvg(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return "0";
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

function StatTile({
  label,
  value,
  sub,
  highlight,
}: {
  label: string;
  value: string;
  sub: string;
  highlight?: boolean;
}) {
  return (
    <Card className={cn(highlight && "border-orange-300 bg-orange-50")}>
      <CardContent className="p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        <p
          className={cn(
            "mt-1 text-2xl font-bold tabular-nums",
            highlight && "text-orange-700",
          )}
        >
          {value}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">{sub}</p>
      </CardContent>
    </Card>
  );
}

function StatTileSkeleton() {
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-7 w-24" />
        <Skeleton className="h-3 w-28" />
      </CardContent>
    </Card>
  );
}

/**
 * Member profile enrichment (Phase 38) — 4-tile stat row consuming
 * `GET /contacts/:id/stats` (useGetContactStats). Pure presentation: takes a
 * contactId, fetches its own data, renders Lifetime Paid / Pending Dues /
 * Total Visits / Member Since. Pending Dues tile gets an orange
 * outline/highlight when pendingDues > 0 (mirrors the mockup's Collect button
 * treatment). Standalone — not wired into member-profile.tsx by this plan
 * (38-09 composes it).
 */
export function MemberStatTiles({ contactId }: { contactId: number }) {
  const { data: stats, isLoading } = useGetContactStats(contactId);

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTileSkeleton />
        <StatTileSkeleton />
        <StatTileSkeleton />
        <StatTileSkeleton />
      </div>
    );
  }

  if (!stats) {
    return <p className="text-sm text-muted-foreground">Stats unavailable</p>;
  }

  const pendingDues = Number(stats.pendingDues ?? 0);
  const invoicesSettled = stats.invoicesSettled ?? 0;

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatTile
        label="Lifetime Paid"
        value={formatINR(stats.lifetimePaid)}
        sub={`${invoicesSettled} invoice${invoicesSettled === 1 ? "" : "s"} settled`}
      />
      <StatTile
        label="Pending Dues"
        value={formatINR(pendingDues)}
        sub={pendingDues > 0 ? `Oldest due ${fmtDate(stats.oldestDueDate)}` : "All caught up"}
        highlight={pendingDues > 0}
      />
      <StatTile
        label="Total Visits"
        value={String(stats.totalVisits ?? 0)}
        sub={`avg ${fmtAvg(stats.avgPerMonth)}/mo · ${fmtAvg(stats.avgPerWeek)}/wk`}
      />
      <StatTile
        label="Member Since"
        value={stats.memberSinceLabel || "—"}
        sub={stats.memberDurationLabel ? `${stats.memberDurationLabel} member` : "—"}
      />
    </div>
  );
}
